class PortalController {
  static #secret;
  static #secretData;

  static async authenticate() {
    PortalController.#secret = UrlRoutes.getPathPartByIndex(0);
    let result;

    try {
      TokenManager.removeToken();
      result = await PortalApiCalls.authenticate(PortalController.#secret);
    } catch (e) {
      ApiError.assertIs(e);
      result = await PortalController.#handleAuthenticationError(e);

      if (!result) {
        return false;
      }
    }

    TokenManager.saveToken(result.token);
    PortalController.#secretData = TokenManager.getTokenData(result.token).context;

    await FormSettings.load();

    return true;
  }

  static goToForm() {
    const routeName = PortalController.#getRouteNameByModelCanAccess();
    PortalController.goWithSecret(routeName);
  }

  static goWithSecret(routeName) {
    UrlRoutes.go(routeName, { secret: PortalController.#secret });
  }

  static get secretData() {
    return PortalController.#secretData;
  }

  static #getRouteNameByModelCanAccess() {
    const token = TokenManager.loadToken();
    const modelCanAccess = TokenManager.getTokenData(token).context;

    return {
      "lid-send-notice": "portal-lid-self-inspection-response",
      "indcom-facility-inspection": "portal-indcom-facility-self-inspection-response",
    }[modelCanAccess.type];
  }

  // Returns credentials when the person accepted a newer notice, otherwise null with the reason
  // already shown to them.
  static async #handleAuthenticationError(e) {
    if (e.status === 403 && e.xhrResponse?.responseJSON) {
      return await PortalController.#offerCurrentNotice(e.xhrResponse.responseJSON);
    }

    PortalController.#showAuthenticationError(e);

    return null;
  }

  static async #offerCurrentNotice(response) {
    if (response.reason !== "expired" || !response.newNoticeAvailable) {
      PortalController.#showForbiddenAuthenticationError(response);

      return null;
    }

    const proceed = await PortalController.#confirmProceedToCurrentNotice();

    if (!proceed) {
      PortalController.#showDeclinedOfferMessage(response.groupName);

      return null;
    }

    try {
      // The old code is sent again; the server resolves which notice it maps to now.
      return await PortalApiCalls.authenticateCurrentNotice(PortalController.#secret);
    } catch (error) {
      ApiError.assertIs(error);
      PortalController.#showAuthenticationError(error);

      return null;
    }
  }

  // A decline leaves the offer on screen so the contact message can replace it in place. Bootstrap
  // shows a modal while the previous one is still hiding, which leaves an untracked backdrop behind
  // that would grey the page out for good once the contact message is dismissed.
  static #confirmProceedToCurrentNotice() {
    const choice = MessageModal.showCustomMessage(
      "A newer notice is available",
      "The code you scanned points to an older notice for this property. A newer notice has " +
        "since been sent. Select “Proceed to new notice” to respond to the current one.",
      {
        primaryButtonText: "Proceed to new notice",
        secondaryButtonText: "Cancel",
        secondaryButtonStaysOpen: true,
      },
    );

    return Promise.race([choice, PortalController.#declineOnDismissal()]);
  }

  // renderMessage clears the container's handlers as it renders, which takes Bootstrap's own Esc
  // and backdrop dismissal with it, so both are rebound here. A dismissal is a decline, which is
  // what the person meant by it.
  static #declineOnDismissal() {
    const $modal = $("#dynamic-warning-modal");
    let pressStartedOnBackdrop = false;

    return new Promise((resolve) => {
      $modal.on("keydown.portalOffer", (e) => e.which === 27 && resolve(false));
      $modal.on("mousedown.portalOffer", (e) => {
        pressStartedOnBackdrop = e.target === e.currentTarget;
      });
      $modal.on(
        "click.portalOffer",
        (e) => pressStartedOnBackdrop && e.target === e.currentTarget && resolve(false),
      );
    });
  }

  static #showDeclinedOfferMessage(groupName) {
    MessageModal.showCustomMessage(
      "Contact your agency",
      "No changes have been made. If you have questions about the notice you received, or you " +
        `still need to respond to it, please contact the ${groupName}.`,
      { primaryButtonText: "Okay" },
    );
  }

  static #showAuthenticationError(e) {
    if (e.status === 401) {
      MessageModal.showCustomMessage(
        "Invalid URL",
        "The URL you've entered is not in our records. Please double check that you've entered the correct code.",
      );
    } else if (e.status === 500 || e.status === 0) {
      MessageModal.showCustomMessage(
        "Server Error",
        "Something went wrong on our end, please try again later or <a href='https://kb.2ndnaturewater.com/knowledge/kb-tickets/new' target='_blank'>contact support</a> for assistance.",
      );
    } else if (e.status === 429) {
      MessageModal.showCustomMessage(
        "Too Many Attempts",
        "Please wait a minute and refresh the page.",
      );
    } else if (e.status === 403 && e.xhrResponse?.responseJSON) {
      PortalController.#showForbiddenAuthenticationError(e.xhrResponse.responseJSON);
    } else {
      throw e;
    }
  }

  static #showForbiddenAuthenticationError(response) {
    const groupName = response.groupName;

    if (response.reason === "expired") {
      MessageModal.showCustomMessage(
        "Invalid URL",
        `Oops! Looks like this URL is no longer valid. If you believe this is a mistake, please contact the ${groupName}.`,
      );
    } else if (response.reason === "completed") {
      MessageModal.showCustomMessage(
        "Already Submitted",
        `Oops! Looks like your response has already been recorded. If you believe this was a mistake, please contact the ${groupName}.`,
      );
    }
  }
}

module.exports = PortalController;

const PortalApiCalls = require("./portalApiCalls");
const UrlRoutes = require("../routes/urlRoutes");
const TokenManager = require("../login/tokenManager");
const ApiError = require("../errors/apiError");
const MessageModal = require("../modals/messageModal");
const FormSettings = require("../settings/formSettings");
